import {memo, ReactElement, useEffect, useMemo, useState} from "react";
import {
    BasicSimpleHoTRow,
    SimpleHoTCellStyle,
    SimpleHoTRow,
    SimpleHoTWrapper
} from "../line-item-management/SimpleHoTWrapper";
import {Property, useProperties} from "../../../contexts/properties/PropertiesContext";
import {Account, useChartOfAccounts} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {toLockLookup} from "../line-item-management/logic";
import {getMonths} from "../../../utils/months-helper";
import {SimpleHoTWrapperControls} from "../line-item-management/SimpleHoTWrapperControls";
import VZSaveStatus from "../../../components/vz-ui/vz-save-status/VZSaveStatus";
import * as css from "./styles/GlobalOverrides.module.scss";
import {AdminWrapper} from "../AdminWrapper";
import AdminHeader from "../AdminHeader";
import {Dropdown, Field, Item, Label, Menu, Select} from "@zendeskgarden/react-dropdowns";
import {Field as FormField, Input as FormInput, Label as FormLabel} from "@zendeskgarden/react-forms";
import {
    GetGlobalOverridesQuery,
    useAllPropertyForecastLocksQuery, useDeleteGlobalOverridesDetailedMutation,
    useGetGlobalOverridesLazyQuery,
    VersionType
} from "../../../__generated__/generated_types";
import {toast} from "react-toastify";

type Override = GetGlobalOverridesQuery["globalOverrides"][0] & {accountName: string, propertyName: string};

interface CollectedOverrideDeletes {
    months: number[];
    propertyAccountIds: {propertyId: string, accountId: string}[];
}

const COL_MAP = new Map<number, number>([
    [2, 0], // Jan
    [3, 1], // Feb
    [4, 2], // Mar
    [5, 3], // Apr
    [6, 4], // May
    [7, 5], // Jun
    [8, 6], // Jul
    [9, 7], // Aug
    [10, 8], // Sep
    [11, 9], // Oct
    [12, 10], // Nov
    [13, 11], // Dec
]);

function matchesFilter(override: Override, parts: string[]): boolean {
    const accountName = override.accountName.toLowerCase();
    const propertyName = override.propertyName.toLowerCase();
    if(parts.length === 1 && parts[0]) {
        return accountName.includes(parts[0].toLowerCase()) ||
                propertyName.includes(parts[0].toLowerCase());
    } else if(parts.length === 2 && parts[0] && parts[1]) {
        return (
                accountName.includes(parts[1].toLowerCase()) &&
                propertyName.includes(parts[0].toLowerCase())
            ) ||
            (
                accountName.includes(parts[0].toLowerCase()) &&
                propertyName.includes(parts[1].toLowerCase())
            );
    }
    return false;
}

export interface GlobalOverridesProps {
    budgetYear: number;
}

export function GlobalOverrides(props: GlobalOverridesProps): ReactElement {
    const [versionType, setVersionType] = useState<VersionType.Budget | VersionType.Reforecast>(VersionType.Budget);

    const {data: locks, loading: locksLoading} = useAllPropertyForecastLocksQuery({
        fetchPolicy: "no-cache",
        variables: {
            budgetYear: props.budgetYear
        }
    });
    const [getOverrides, {data: overrides, loading: overridesLoading}] = useGetGlobalOverridesLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [deleteOverrides, {loading: deleteLoading}] = useDeleteGlobalOverridesDetailedMutation({
        fetchPolicy: "no-cache"
    });

    const {properties} = useProperties();
    const {accounts} = useChartOfAccounts();

    const [deleteTime, setDeleteTime] = useState<number>(0);
    const [collectedDeletes, setCollectedDeletes] = useState<CollectedOverrideDeletes[]>([]);
    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const [tmpFilter, setTmpFilter] = useState<string | undefined>(undefined);
    const [filter, setFilter] = useState<string | undefined>(undefined);

    useEffect(() => {
        if(collectedDeletes.length === 0 || deleteLoading) {
            return;
        }

        const local = [...collectedDeletes];
        setCollectedDeletes([]);
        deleteOverrides({
            variables: {
                budgetYear: props.budgetYear,
                versionType: versionType,
                details: local.flatMap(({propertyAccountIds, months}) => {
                    return propertyAccountIds.map(({propertyId, accountId}) => ({
                        propertyId: propertyId,
                        accountId: accountId,
                        monthIndexes: months
                    }));
                })
            }
        }).then(res => {
            if(res.data?.deleteGlobalOverridesDetailed) {
                setLastSaved(new Date());
                toast.success("Overrides deleted");
                setDeleteTime(Date.now());
                return;
            }
            toast.error("Failed to delete overrides");
        }).catch(() => {
            toast.error("Failed to delete overrides");
        });
    }, [collectedDeletes, deleteLoading]);

    useEffect(() => {
        getOverrides({
            variables: {
                budgetYear: props.budgetYear,
                versionType: versionType
            }
        });
    }, [props.budgetYear, versionType, deleteTime]);

    useEffect(() => {
        const to = setTimeout(() => {
            setFilter(tmpFilter);
        }, 500);
        return () => clearTimeout(to);
    }, [tmpFilter]);

    const propertyIdMap = useMemo<Record<string, Property>>(() => {
        if(!properties) {
            return {};
        }
        return properties.toIdMap(p => p.id);
    }, [properties]);

    const accountsIdMap = useMemo<Record<string, Account>>(() => {
        return accounts.toIdMap(a => a.id);
    }, [accounts]);

    const [rows, setRows] = useState<Override[]>([]);
    useEffect(() => {
        if(overridesLoading || !overrides) {
            return;
        }

        const localRows = overrides.globalOverrides.map(o => {
            const account = accountsIdMap[o.accountId];
            let accountName = "";
            if(account) {
                accountName = `${account.accountNumber} ${account.name}`;
            }
            return {
                ...o,
                accountName: accountName,
                propertyName: propertyIdMap[o.propertyId]?.name ?? ""
            };
        }).sortBy(o => `${o.propertyName} ${o.accountName}`);

        setLastSaved(new Date());
        setRows(localRows);
    }, [overrides, overridesLoading, propertyIdMap, accountsIdMap]);

    const [simpleHoTRows, setSimpleHoTRows] = useState<SimpleHoTRow[]>([]);

    useEffect(() => {
        const hotRows: BasicSimpleHoTRow[] = [];
        let parts: string[] = [];
        if(filter) {
            parts = filter.split(" ");
        }

        for(const override of rows) {
            if(parts.length > 0 && !matchesFilter(override, parts)) {
                continue;
            }

            hotRows.push({
                isCompound: false,
                values: {
                    property: override.propertyName,
                    account: override.accountName,
                    values: override.values
                },
                extraData: {
                    override: override
                }
            });
        }
        setSimpleHoTRows(hotRows);
    }, [rows, filter]);

    const locksLookup = useMemo<Set<string>>(() => {
        return toLockLookup(versionType, locks);
    }, [versionType, locks]);

    function doDeleteOverrides(months: number[], propertyAccountIds: {propertyId: string, accountId: string}[]): void {
        setCollectedDeletes(collectedDeletes.concat({
            months: months,
            propertyAccountIds: propertyAccountIds
        }));
    }

    const months = getMonths(properties?.[0] ?? undefined);
    return <AdminWrapper>
        <AdminHeader
                title={"Global Overrides"}
                subtitle={"View all overrides on all accounts."}
        />
        <div className={css.tableWrapper}>
             <div className={css.globalOverridesWrapper}>
                <SimpleHoTWrapperControls>
                    <div className={css.globalOverridesControls}>
                        <div>
                            <FormField>
                                <FormLabel>Filter Override</FormLabel>
                                <FormInput
                                        value={tmpFilter ?? ""}
                                        onChange={e => setTmpFilter(e.target.value ?? undefined)}
                                />
                            </FormField>
                        </div>
                        <div className={css.actions}>
                            <div className={css.saveStatus}>
                                <VZSaveStatus isSaving={false} lastSaved={lastSaved} />
                            </div>
                            <Dropdown onSelect={type => setVersionType(type as VersionType.Budget | VersionType.Reforecast)}>
                                <Field style={{display: "inline-block", position: "absolute", width: "10rem", right: "1rem", bottom: "1rem"}}>
                                    <Label hidden>Forecast Period</Label>
                                    <Select>{versionType === VersionType.Budget ? 'Budget' : 'Reforecast'}</Select>
                                </Field>
                                <Menu>
                                    <Item value={VersionType.Budget}>Budget</Item>
                                    <Item value={VersionType.Reforecast}>Reforecast</Item>
                                </Menu>
                            </Dropdown>
                        </div>
                    </div>
                </SimpleHoTWrapperControls>
                <MemoizedSimpleHoTWrapper
                    withControls
                    openSectionsPersistOnRender={true}
                    dataLoading={overridesLoading || locksLoading}
                    rows={simpleHoTRows}
                    nestedRows={false}
                    columns={[
                        {data: "property"},
                        {data: "account"},
                        {data: "values.0", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.1", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.2", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.3", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.4", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.5", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.6", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.7", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.8", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.9", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.10", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                        {data: "values.11", type: "numeric", numericFormat: {pattern: '$0,0.00'}, allowInvalid: false},
                    ]}
                    cellStyler={(_row, col, rowData) => {
                        const styles: SimpleHoTCellStyle = {
                            isReadOnly: true
                        };



                        if(col === 0 || col === 1) {
                            styles.cssClasses = [css.leftTextColumn];

                            if(col === 0) {
                                const override = rowData.extraData?.["override"] as Override | undefined;
                                if(override) {
                                    const locked = locksLookup.has(override.propertyId);
                                    if(locked) {
                                        styles.cssClasses.push("lockedColumn");
                                    }
                                }
                            }
                        }

                        return styles;
                    }}
                    hotProps={{
                        renderAllRows: false,
                        height: "50vh",
                        colHeaders: ["Property", "Account", ...months],
                        colWidths: [180, 150, ...(new Array(12).fill(90))],
                        autoRowSize: false,
                        autoColumnSize: false,
                        fixedColumnsLeft: 2,
                        rowHeights: 40,
                        undo: false
                    }}
                    contextMenu={{
                        "delete": {
                            name: "Undo Override",
                            hidden: () => false,
                            disabled: (_rows, _cols, data) => {
                                for(const sel of data) {
                                    const override = sel.rowData.extraData?.["override"] as Override | undefined;
                                    if(override) {
                                        const locked = locksLookup.has(override.propertyId);
                                        if(locked) {
                                            return true;
                                        }
                                    }
                                }
                                return false;
                            },
                            callback: (_rows, cols, data) => {
                                const months: number[] = [];
                                for(const col of Array.from(cols)) {
                                    const month = COL_MAP.get(col);
                                    if(month !== undefined) {
                                        months.push(month);
                                    }
                                }

                                const propertyAccountIds: {propertyId: string, accountId: string}[] = [];
                                for(const sel of data) {
                                    const override = sel.rowData.extraData?.["override"] as Override | undefined;
                                    if(override) {
                                        propertyAccountIds.push({propertyId: override.propertyId, accountId: override.accountId});
                                    }
                                }
                                doDeleteOverrides(months, propertyAccountIds);
                            }
                        }
                    }}
                />
            </div>
        </div>
    </AdminWrapper>;
}

const MemoizedSimpleHoTWrapper = memo(SimpleHoTWrapper, (prevProps, nextProps) => {
    // Everything but rows is static.
    return prevProps.rows === nextProps.rows;
});